import { Reveal, SectionLabel, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { GraduationCap, Award } from "lucide-react";

const highlights = [
  "Identifying and mitigating security risks in systems and networks",
  "Ethical hacking and penetration testing fundamentals",
  "Threat landscape: malware, phishing, DoS, and post-exploitation",
  "Firewalls, antivirus, and intrusion detection systems",
  "Encryption and secure data transmission principles",
  "Digital forensics — investigating and reconstructing incidents",
  "Security policies, protocols, and compliance frameworks",
  "Data privacy regulations and IT-security project management",
];

const activities = [
  "Phi Theta Kappa Honor Society",
  "SF College National Cyber League Team · Fall 2022",
  "Santa Fe College IT Club",
];

export default function Education() {
  return (
    <section
      id="education"
      data-testid="education-section"
      className="relative py-24 lg:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel id="SYS_07">EDUCATION // FOUNDATION</SectionLabel>

        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-4 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#00FF66]">
                <GraduationCap size={14} strokeWidth={1.5} />
                SANTA_FE_COLLEGE
              </div>
              <h2 className="font-['Unbounded'] font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F5F5F5] leading-[1.05]">
                Associate of Science.
                <br />
                <span className="text-[#A1A1AA]">IT Security &</span>{" "}
                <span className="text-[#00FF66]">Risk Management</span>.
              </h2>
              <p className="mt-5 font-['JetBrains_Mono'] text-[11px] tracking-[0.25em] text-[#A1A1AA]">
                JAN 2020 → AUG 2024
              </p>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <StatCell k="4.00" v="GPA" />
                <StatCell k="4.5 YRS" v="PROGRAM_LENGTH" />
              </div>

              <div className="mt-8">
                <div className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#00FF66] mb-3">
                  [ACTIVITIES]
                </div>
                <ul className="space-y-2">
                  {activities.map((a) => (
                    <li
                      key={a}
                      className="flex items-start gap-3 text-[#D4D4D8] text-sm leading-relaxed"
                    >
                      <Award size={12} strokeWidth={1.5} className="text-[#00FF66] mt-1 shrink-0" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="border border-white/10 bg-[#0A0A0A] p-6 lg:p-8">
                <div className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#00FF66] mb-4">
                  [PROGRAM_OUTCOMES]
                </div>
                <p className="text-[#A1A1AA] leading-relaxed text-sm">
                  Career-focused program covering cybersecurity fundamentals,
                  ethical hacking, network defense, and risk assessment. Prepared
                  and passed CompTIA CySA+, PenTest+, Security+, and Network+
                  during the program.
                </p>

                <StaggerGroup className="mt-6 grid grid-cols-1 gap-2" stagger={0.05}>
                  {highlights.map((h, i) => (
                    <StaggerItem key={h} y={8}>
                      <div className="flex items-start gap-4 py-2 border-b border-white/5 last:border-b-0">
                        <span className="font-['JetBrains_Mono'] text-[10px] tracking-[0.25em] text-[#00FF66] pt-1 shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="text-[#D4D4D8] text-sm leading-relaxed">
                          {h}
                        </span>
                      </div>
                    </StaggerItem>
                  ))}
                </StaggerGroup>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function StatCell({ k, v }) {
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-4">
      <div className="font-['Unbounded'] font-black text-2xl text-[#00FF66]">{k}</div>
      <div className="mt-1 font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#A1A1AA]">
        {v}
      </div>
    </div>
  );
}
